import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { grnApi } from "../../api";
import { ProcError, ProcForm, ProcSection } from "../../components/ProcurementLayout";
import { useMutation } from "../../hooks";
import type { GRNLine } from "../../types";

type LineRow = Omit<GRNLine, "id">;

const emptyLine = (order: number): LineRow => ({
  lineOrder: order,
  description: "",
  qtyOrdered: 0,
  qtyReceived: 0,
  remarks: "",
  itemType: "CONSUMABLE",
  inventoryItemId: null,
  assetCategoryId: null,
});

export default function GRNCreatePage() {
  const navigate = useNavigate();

  const [lpoId, setLpoId] = useState<number | "">("");
  const [receivedFrom, setReceivedFrom] = useState("");
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  const [lines, setLines] = useState<LineRow[]>([emptyLine(1)]);

  const { mutate, loading, error } = useMutation(async () => {
    if (!lpoId) throw new Error("LPO ID is required.");
    if (!receivedFrom.trim()) throw new Error("Received from is required.");
    if (!deliveryNoteNo.trim()) throw new Error("Delivery note number is required.");
    const result = await grnApi.create({
      lpoId: lpoId as number,
      receivedFrom,
      deliveryNoteNo,
      lines: lines.map((l) => ({ lineOrder: l.lineOrder, description: l.description, qtyOrdered: l.qtyOrdered, qtyReceived: l.qtyReceived, remarks: l.remarks, itemType: l.itemType ?? "CONSUMABLE", inventoryItemId: l.inventoryItemId ?? undefined, assetCategoryId: l.assetCategoryId ?? undefined })),
    });
    navigate(`/procurement/grns/${result.id}`);
  });

  function addLine() {
    setLines((prev) => [...prev, emptyLine(prev.length + 1)]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineOrder: i + 1 })));
  }

  function updateLine(idx: number, field: keyof LineRow, value: string | number) {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/grns" className="text-sm text-emerald-700 hover:underline">&larr; Back to GRNs</Link>
      </div>

      <ProcForm formNo="PROC-FRM-05" title="Goods Received Note">
        {error && <ProcError message={error} />}

        <ProcSection title="GRN Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">LPO ID *</label>
              <input type="number" value={lpoId} onChange={(e) => setLpoId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Received From *</label>
              <input type="text" value={receivedFrom} onChange={(e) => setReceivedFrom(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Delivery Note No *</label>
              <input type="text" value={deliveryNoteNo} onChange={(e) => setDeliveryNoteNo(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left w-8">#</th>
                  <th className="px-2 py-2 text-left">Description</th>
                  <th className="px-2 py-2 text-left w-32">Item Type</th>
                  <th className="px-2 py-2 text-right w-24">Qty Ordered</th>
                  <th className="px-2 py-2 text-right w-24">Qty Received</th>
                  <th className="px-2 py-2 text-right w-20">Variance</th>
                  <th className="px-2 py-2 text-left">Remarks</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((line, idx) => {
                  const variance = line.qtyReceived - line.qtyOrdered;
                  return (
                    <tr key={idx}>
                      <td className="px-2 py-2 text-slate-400">{line.lineOrder}</td>
                      <td className="px-2 py-2">
                        <input type="text" value={line.description} onChange={(e) => updateLine(idx, "description", e.target.value)} placeholder="Item" className="w-full rounded border px-2 py-1 text-xs" />
                      </td>
                      <td className="px-2 py-2">
                        <select value={line.itemType ?? "CONSUMABLE"} onChange={(e) => updateLine(idx, "itemType", e.target.value)}
                          className="w-full rounded border px-2 py-1 text-xs">
                          <option value="CONSUMABLE">Consumable</option>
                          <option value="CAPITAL_ASSET">Capital Asset</option>
                          <option value="SERVICE">Service</option>
                          <option value="WORKS">Works</option>
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={line.qtyOrdered} min={0} onChange={(e) => updateLine(idx, "qtyOrdered", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={line.qtyReceived} min={0} onChange={(e) => updateLine(idx, "qtyReceived", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className={`px-2 py-2 text-right font-semibold text-xs ${variance < 0 ? "text-red-600" : "text-slate-700"}`}>
                        {variance >= 0 ? "+" : ""}{variance}
                      </td>
                      <td className="px-2 py-2">
                        <input type="text" value={line.remarks ?? ""} onChange={(e) => updateLine(idx, "remarks", e.target.value)} placeholder="Optional" className="w-full rounded border px-2 py-1 text-xs" />
                      </td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => removeLine(idx)} disabled={lines.length === 1} className="text-red-400 hover:text-red-600 disabled:opacity-30 text-xs">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addLine} className="mt-3 rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
            + Add Line
          </button>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/grns" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save GRN"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
